/**
 * Forms — the Agent Form Workspace list (trec-svc /trec/forms).
 *
 * Staff (agent / brokerage_admin / super_admin) create TREC forms for a client,
 * then open one to auto-fill from the client's uploaded-document extractions,
 * edit, and send for DocuSign e-signature. This screen is the entry point: a
 * filterable list + a "New form" creator. The detail screen does the rest.
 *
 * Forms are agent-scoped server-side (resolve_agent_scope), so an agent only
 * sees their own clients' forms — no extra client-side filtering needed.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  clientService,
  formService,
  type ClientRow,
  type FormOut,
  type FormStatus,
  type FormType,
} from "@re-platform/api-client";
import { Button, Card, Empty, StatusPill, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";
import { Avatar, cardList, cardShell, Chevron } from "../../lib/cards";
import {
  FORM_TYPES,
  formTypeLabel,
  statusLabel,
  statusTone,
} from "./formHelpers";

function clientName(c: ClientRow): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || c.id.slice(0, 8);
}

export function Forms() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [toast, setToast] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<FormStatus | "">("");
  const [creating, setCreating] = useState(false);

  const formsQuery = useQuery({
    queryKey: ["forms", clientFilter || null, statusFilter || null],
    queryFn: () =>
      formService.list(api, {
        ...(clientFilter ? { client_id: clientFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
  });

  // Forms waiting on the AGENT. A listing agreement is held at PREFILLED because
  // its ¶2A legal description is the agent's to complete, and until #21 nothing
  // anywhere said so — no status filter, no card. A state nothing surfaces is a
  // state nobody acts on (gap I26, the same shape as AWAITING_APPROVAL at #18),
  // and here the whole packet waits behind it, so the client sees nothing at all.
  const waitingQuery = useQuery({
    queryKey: ["forms", "waiting-on-agent"],
    queryFn: () => formService.list(api, { status: "PREFILLED" }),
  });
  const waitingCount = waitingQuery.data?.length ?? 0;

  // Client roster — powers both the filter and the "New form" client picker.
  // May 422 for a super_admin with no tenant selected; degrade gracefully.
  const clientsQuery = useQuery({
    queryKey: ["clients"],
    queryFn: () => clientService.list(api),
    retry: false,
  });

  const clients = clientsQuery.data ?? [];
  const clientNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clients) m.set(c.id, clientName(c));
    return m;
  }, [clients]);

  const forms = formsQuery.data ?? [];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h1 style={{ marginTop: 0 }}>Forms</h1>
        <Button onClick={() => setCreating(true)} disabled={creating}>
          New form
        </Button>
      </div>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
        Create a TREC form, auto-fill it from the client's uploaded documents,
        then send it for e-signature.
      </p>

      {creating && (
        <NewFormCard
          clients={clients}
          onCancel={() => setCreating(false)}
          onCreated={(form) => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ["forms"] });
            navigate(`/forms/${form.id}`);
          }}
          onError={(msg) => setToast(msg)}
        />
      )}

      {waitingCount > 0 && statusFilter !== "PREFILLED" && (
        <Card padding="md" style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <strong>
                {waitingCount} document{waitingCount === 1 ? "" : "s"} waiting
                on you
              </strong>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 13,
                  color: "var(--rp-color-text-muted)",
                }}
              >
                The client can't sign anything on these applications until you
                finish and send them.
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => setStatusFilter("PREFILLED")}
            >
              Review them
            </Button>
          </div>
        </Card>
      )}

      <Card padding="md" style={{ marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="client-filter" style={{ fontSize: 13 }}>
              Filter by client
            </label>
            <select
              id="client-filter"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              style={{ padding: "6px 8px", font: "inherit", fontSize: 13 }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {clientName(c)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="status-filter" style={{ fontSize: 13 }}>
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as FormStatus | "")
              }
              style={{ padding: "6px 8px", font: "inherit", fontSize: 13 }}
            >
              <option value="">Any status</option>
              <option value="PREFILLED">Waiting on you</option>
              <option value="DRAFT">Draft</option>
              <option value="SENT_FOR_SIGNATURE">Sent for signature</option>
              <option value="SIGNED">Signed</option>
            </select>
          </div>
        </div>
      </Card>

      {formsQuery.isError && (
        <Card padding="lg" style={{ marginTop: 16 }}>
          <p style={{ margin: 0 }}>
            Couldn't load forms: {(formsQuery.error as Error).message}.
          </p>
        </Card>
      )}

      {formsQuery.isSuccess && forms.length === 0 && (
        <div style={{ marginTop: 24 }}>
          <Empty
            title="No forms yet"
            description="Create a form to auto-fill it from a client's documents."
            action={<Button onClick={() => setCreating(true)}>New form</Button>}
          />
        </div>
      )}

      {forms.length > 0 && (
        <div style={{ ...cardList, marginTop: 16 }}>
          {forms.map((f: FormOut) => {
            const client =
              clientNames.get(f.client_id) ?? f.client_id.slice(0, 8);
            return (
              <Link
                key={f.id}
                to={`/forms/${f.id}`}
                className="rp-clientcard"
                style={{
                  ...cardShell,
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                {/* The avatar carries the CLIENT, not the form: a form is only
                    ever read as "whose is it", and the form type is already
                    spelled out beside it. */}
                <Avatar name={client} />
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {formTypeLabel(f.form_type)}
                  </div>
                  <div
                    style={{
                      color: "var(--rp-color-text-muted)",
                      fontSize: 13,
                      marginTop: 2,
                    }}
                  >
                    {client} ·{" "}
                    <code style={{ fontSize: 12 }}>{f.template_version}</code> ·
                    updated {new Date(f.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <StatusPill tone={statusTone(f.status)}>
                  {statusLabel(f.status)}
                </StatusPill>
                <Chevron />
              </Link>
            );
          })}
        </div>
      )}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

function NewFormCard({
  clients,
  onCancel,
  onCreated,
  onError,
}: {
  clients: readonly ClientRow[];
  onCancel: () => void;
  onCreated: (form: FormOut) => void;
  onError: (msg: string) => void;
}) {
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [formType, setFormType] = useState<FormType>(FORM_TYPES[0]!.value);
  const [busy, setBusy] = useState(false);

  async function create() {
    if (!clientId) {
      onError("Pick a client first.");
      return;
    }
    setBusy(true);
    try {
      const form = await formService.create(api, {
        client_id: clientId,
        form_type: formType,
      });
      await queryClient.invalidateQueries({ queryKey: ["forms"] });
      onCreated(form);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Couldn't create the form.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card padding="lg" style={{ marginTop: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 16 }}>New form</h2>
      {clients.length === 0 && (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          No clients available — a super-admin must pick a brokerage first
          (create forms from a brokerage-admin or agent login).
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "end",
        }}
      >
        <label style={{ fontSize: 13 }}>
          Client
          <br />
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            style={{ padding: "6px 8px", font: "inherit", marginTop: 4 }}
          >
            <option value="">Select a client…</option>
            {clients.map((c) => {
              const name =
                [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
                c.email ||
                c.id.slice(0, 8);
              return (
                <option key={c.id} value={c.id}>
                  {name}
                </option>
              );
            })}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>
          Form type
          <br />
          <select
            value={formType}
            onChange={(e) => setFormType(e.target.value as FormType)}
            style={{ padding: "6px 8px", font: "inherit", marginTop: 4 }}
          >
            {FORM_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <Button onClick={create} disabled={busy || !clientId}>
          {busy ? "Creating…" : "Create"}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
